// File: src/features/jobs/McpStatusPanel.tsx
// MCP Status Panel for Job Monitoring Dashboard
//
// ✅ PROVIDES: MCP health status, provider availability, and capabilities overview
// ✅ INTEGRATES: Real-time MCP monitoring with job management dashboard
// ✅ DISPLAYS: Multi-AI provider status and orchestration capabilities

import { Badge } from '@/components/ui/badge';
import { useMcpHealth } from './useMcpJobManager';

interface McpStatusPanelProps {
  className?: string;
  showDetails?: boolean;
}

export default function McpStatusPanel({ 
  className = "", 
  showDetails = false 
}: McpStatusPanelProps) {
  const { health, isHealthy, capabilities, providers } = useMcpHealth();

  // Get provider status summary
  const getProviderSummary = () => {
    if (!providers) return { total: 0, healthy: 0 };
    
    const providerEntries = Object.entries(providers);
    return {
      total: providerEntries.length,
      healthy: providerEntries.filter(([, available]) => available).length
    };
  };

  const providerSummary = getProviderSummary();

  // Get capabilities summary
  const getEnabledCapabilities = () => {
    if (!capabilities) return [];
    return Object.entries(capabilities)
      .filter(([, enabled]) => enabled)
      .map(([capability]) => capability);
  };

  const enabledCapabilities = getEnabledCapabilities();

  return (
    <div className={`bg-gradient-to-br from-purple-50 to-lex-accent-soft dark:from-purple-900/10 dark:to-lex-accent-soft rounded-lg border border-purple-200 dark:border-purple-800 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200">
              MCP Integration Status
            </h3>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              Multi-AI orchestration platform
            </p>
          </div>
        </div>
        
        <Badge variant={isHealthy ? 'success' : 'destructive'}>
          {health?.status || 'unknown'}
        </Badge>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {/* Provider Status */}
        <div className="text-center">
          <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
            {providerSummary.healthy}/{providerSummary.total}
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            AI Providers
          </div>
        </div>

        {/* Capabilities */}
        <div className="text-center">
          <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
            {enabledCapabilities.length}
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            Capabilities
          </div>
        </div>

        {/* Enhanced Jobs */}
        <div className="text-center">
          <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
            {isHealthy ? '✅' : '❌'}
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            Enhanced Analysis
          </div>
        </div>

        {/* Orchestration */}
        <div className="text-center">
          <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
            {capabilities?.multiAi ? '🚀' : '🔄'}
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            Multi-AI
          </div>
        </div>
      </div>

      {/* Detailed Information */}
      {showDetails && health && (
        <div className="space-y-3">
          {/* Provider Details */}
          {providers && Object.keys(providers).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">
                AI Provider Status
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(providers).map(([provider, available]) => (
                  <div key={provider} className="flex items-center gap-2">
                    <span className="text-xs">
                      {available ? '🟢' : '🔴'}
                    </span>
                                         <Badge variant={available ? 'success' : 'outline'}>
                       {provider}
                     </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Capabilities Details */}
          {enabledCapabilities.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">
                Active Capabilities
              </h4>
              <div className="flex flex-wrap gap-1">
                {enabledCapabilities.map((capability) => (
                                   <Badge key={capability} variant="secondary">
                   {capability.replace(/([A-Z])/g, ' $1').toLowerCase()}
                 </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Service Info */}
          <div className="pt-2 border-t border-purple-200 dark:border-purple-700 text-xs text-purple-600 dark:text-purple-400">
            <div className="flex justify-between">
              <span>Service: {health.service}</span>
              <span>Version: {health.version}</span>
            </div>
            <div className="mt-1">
              Updated: {new Date(health.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {!isHealthy && health?.status && (
        <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-sm">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <span>⚠️</span>
            <span>MCP services are currently unavailable</span>
          </div>
          <p className="text-red-600 dark:text-red-400 text-xs mt-1">
            Jobs will use fallback processing without multi-AI enhancement
          </p>
        </div>
      )}

      {/* Loading State */}
      {!health && (
        <div className="flex items-center justify-center py-4">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
            <span className="text-purple-600 dark:text-purple-400 text-sm">
              Checking MCP status...
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Export a compact version for dashboard widgets
export function McpStatusWidget() {
  return <McpStatusPanel className="h-fit" showDetails={false} />;
}

// Export a detailed version for dedicated monitoring pages
export function McpStatusDetails() {
  return <McpStatusPanel showDetails={true} />;
} 